import React from 'react';
import { Card, StatTile, Empty, HowCalculated, DefinitionTable } from '../MetricsUI';
import { Histogram, CountTile } from '../cohort/charts';
import Section from '../Section';
import { OrgLink } from './adminShared';
import { SHOW_LEADERBOARDS } from '../metricsFlags';

/*
 * Section 3 — does the product work.
 *
 * Deliberately narrower than the design prototype, which built a score-by-attempt
 * curve that discarded any attempt number with fewer than 20 observations, an
 * assessment-correlation scatter, and a heavy-versus-light-user split. Each
 * answered a narrow question with wide error bars, and two of them were plainly
 * correlational.
 *
 * What is here instead is the general question: across every learner on the
 * platform, how much did scores move, and which organisations are moving.
 */

export default function Efficacy({ data, onOpenOrg }) {
  const { efficacy: e, improvementByOrg, organizations: orgs } = data;
  const byId = new Map(orgs.map((o) => [o.organizationId, o]));

  const pts = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${Math.round(v)} pts`);
  const share = (n) => (e.measurable ? Math.round((100 * n) / e.measurable) : 0);

  // Improving / flat / regressing hover to their organisations. Learner names
  // are deliberately absent here: this is the platform view, and a manager's own
  // page is where individuals belong.
  const orgsWith = (test) =>
    improvementByOrg.filter((o) => test(o.value)).map((o) => byId.get(o.organizationId) || o);

  return (
    <Section
      title="Efficacy"
      subtitle="Whether scores move, pooled across every learner on the platform"
      summary={
        <div className="flex flex-wrap gap-4">
          <StatTile
            label="Median improvement"
            value={pts(e.stat.median)}
            tone={e.stat.median > 0 ? 'good' : 'default'}
            hint={e.measurable ? `mean ${pts(e.stat.mean)} · n=${e.measurable}` : 'nothing measurable yet'}
            help={
              <>
                <b className="block text-navy">&ldquo;Points&rdquo; means percentage points.</b>
                <ul className="ml-4 mt-1 list-disc space-y-1">
                  <li>Each attempt is scored as a % of that case study&rsquo;s own maximum.</li>
                  <li>Improvement = mean of the last 3 attempts minus mean of the first 3.</li>
                  <li>
                    <b>+13 pts</b> means 50% became 63% — not 13% better, and not 13 marks.
                  </li>
                </ul>
              </>
            }
          />
          <CountTile
            label="Improving"
            value={e.improving}
            tone="good"
            hint={`${share(e.improving)}% · more than +${e.bandPts} pts`}
            items={orgsWith((v) => v > e.bandPts)}
            onOpen={(id) => onOpenOrg(byId.get(id))}
          />
          <CountTile
            label="Flat"
            value={e.flat}
            hint={`${share(e.flat)}% · within ±${e.bandPts} pts`}
            items={orgsWith((v) => v >= -e.bandPts && v <= e.bandPts)}
            onOpen={(id) => onOpenOrg(byId.get(id))}
          />
          <CountTile
            label="Regressing"
            value={e.regressing}
            tone={e.regressing ? 'bad' : 'default'}
            hint={`${share(e.regressing)}% · worse than −${e.bandPts} pts`}
            items={orgsWith((v) => v < -e.bandPts)}
            onOpen={(id) => onOpenOrg(byId.get(id))}
          />
          <StatTile
            label="Not measurable"
            value={e.notMeasurable}
            unit={`/ ${e.learners}`}
            tone={e.notMeasurable > e.measurable ? 'warn' : 'default'}
            help={
              <ul className="ml-4 list-disc space-y-1">
                <li>Improvement needs two scored attempts at the SAME case study.</li>
                <li>Anyone who never retried one cannot be measured, and is excluded.</li>
                <li>
                  So the figures above describe the people who came back — keen learners are
                  over-represented by construction.
                </li>
              </ul>
            }
          />
        </div>
      }
    >
      <div className={`grid gap-6 ${SHOW_LEADERBOARDS ? 'lg:grid-cols-2' : ''}`}>
        <Card>
          <h3 className="mb-4 text-lg font-semibold text-navy">
            How much learners moved
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['X axis', 'Improvement in percentage points — 0 means no change'],
                  ['Left of zero', 'Scores went down'],
                  ['Bar height', 'How many learners fall in that 10-point band'],
                  ['Navy rule', 'Median'],
                  ['Dashed red', 'Mean — apart from the median means a tail is dragging it'],
                ]}
              />
              <span className="block text-gray-500">
                Every learner across every organisation, one value each.
              </span>
            </HowCalculated>
          </h3>
          {e.measurable === 0 ? (
            <Empty>Nobody on the platform has two scored attempts at the same case study yet.</Empty>
          ) : (
            <Histogram histogram={e.histogram} stat={e.stat} unit=" pts" series="improvement" />
          )}
        </Card>

        {/*
          Org-vs-org, ranked worst first — a comparison of named organisations,
          so it goes behind the same flag as the leaderboards. The platform-wide
          histogram beside it answers "is the product working" without ranking
          anyone, which is what this section is actually for.
        */}
        {SHOW_LEADERBOARDS && (
        <Card>
          <h3 className="mb-1 text-lg font-semibold text-navy">
            Which organisations are moving
            <HowCalculated wide label="What is plotted">
              <DefinitionTable
                rows={[
                  ['Bar', "That organisation's average per-learner improvement"],
                  ['Green / red', 'Above or below zero'],
                  ['n', 'Learners with enough attempts to measure'],
                  ['Order', 'Worst first — the accounts not moving are the point'],
                ]}
              />
            </HowCalculated>
          </h3>
          <p className="mb-3 text-xs text-gray-500">Worst first · click a name to open it</p>
          {improvementByOrg.length === 0 ? (
            <Empty>No organisation has measurable improvement yet.</Empty>
          ) : (
            <OrgImprovementBars rows={improvementByOrg} byId={byId} onOpenOrg={onOpenOrg} />
          )}
        </Card>
        )}
      </div>
    </Section>
  );
}

/**
 * A zero-centred bar per organisation.
 *
 * Centred on purpose: a regressing account has to read as being on the other
 * side of a line, not just as a shorter bar than everyone else.
 */
function OrgImprovementBars({ rows, byId, onOpenOrg }) {
  const max = Math.max(10, ...rows.map((r) => Math.abs(r.value)));

  return (
    <div className="space-y-1.5">
      {rows.map((r) => {
        const halfPct = (Math.abs(r.value) / max) * 50;
        const up = r.value >= 0;
        return (
          <div key={r.organizationId} className="flex items-center gap-2">
            <span className="w-40 shrink-0 truncate text-xs">
              <OrgLink org={byId.get(r.organizationId) || r} onOpenOrg={onOpenOrg} />
            </span>
            <div className="relative h-4 flex-1 rounded bg-gray-100">
              <div className="absolute left-1/2 top-0 h-full w-px bg-gray-300" />
              <div
                className={`absolute top-0 h-full ${up ? 'rounded-r bg-emerald-500' : 'rounded-l bg-rose-400'}`}
                style={up ? { left: '50%', width: `${halfPct}%` } : { right: '50%', width: `${halfPct}%` }}
              />
            </div>
            <span
              className={`w-16 shrink-0 text-right text-xs font-semibold tabular-nums ${
                up ? 'text-emerald-600' : 'text-rose-600'
              }`}
            >
              {up ? '+' : ''}
              {Math.round(r.value)} pts
            </span>
            <span className="w-10 shrink-0 text-right text-[11px] text-gray-400">n={r.measurableN}</span>
          </div>
        );
      })}
    </div>
  );
}
